import { RowCrudStatus } from "../../common/types";
import {
  TenantWidgetAccess,
  TenantWidgetAccessCreateMutationVariables,
  TenantWidgetAccessUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { dashboardWidgetDisplayId } from "../../dashboardWidget/store/DashboardWidgetCommonStore";
import { tenantDisplayId } from "../../tenant/store/TenantCommonStore";
import { safeJsonParse } from "../../../common/utils";

export type TenantWidgetAccessValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  __tenant: string;
  showTenant: { displayId: string; id: string } | null;
  __widget: string;
  showWidget: { displayId: string; id: string } | null;
  enabled: boolean;
  configOverride: string;
};

export type TenantWidgetAccessErrorsType = {
  tenant?: { message: string; fieldLabel: string };
  widget?: { message: string; fieldLabel: string };
  enabled?: { message: string; fieldLabel: string };
  configOverride?: { message: string; fieldLabel: string };
};

export function TenantWidgetAccessValuesInit(): TenantWidgetAccessValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    __tenant: "",
    showTenant: null,
    __widget: "",
    showWidget: null,
    enabled: false,
    configOverride: "{}",
  };
}

export type TenantWidgetAccessType =
  | TenantWidgetAccess
  | TenantWidgetAccessValuesType;

export function tenantWidgetAccessDisplayId(
  source: Partial<TenantWidgetAccess>,
  _langStore: LangStore
) {
  return source.widget
    ? dashboardWidgetDisplayId(source.widget, _langStore)
    : _langStore.emptyStringSymbol;
}

export function getTenantWidgetAccessValues(
  source: TenantWidgetAccessType,
  _langStore: LangStore,
  _duplicated: boolean = false
): TenantWidgetAccessValuesType {
  const entityValues = TenantWidgetAccessValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : tenantWidgetAccessDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.showTenant =
    "tenant" in source && source.tenant
      ? {
          id: source.tenant.id,
          displayId: tenantDisplayId(source.tenant, _langStore),
        }
      : "showTenant" in source && source.showTenant
      ? { id: source.showTenant.id, displayId: source.showTenant.displayId }
      : null;
  entityValues.__tenant = entityValues.showTenant?.displayId || "";
  entityValues.showWidget =
    "widget" in source && source.widget
      ? {
          id: source.widget.id,
          displayId: dashboardWidgetDisplayId(source.widget, _langStore),
        }
      : "showWidget" in source && source.showWidget
      ? { id: source.showWidget.id, displayId: source.showWidget.displayId }
      : null;
  entityValues.__widget = entityValues.showWidget?.displayId || "";
  entityValues.enabled = source.enabled || false;
  entityValues.configOverride = source.configOverride || "";
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getTenantWidgetAccessUpdateVariables(
  values: TenantWidgetAccessValuesType
): TenantWidgetAccessUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    tenantId: values.showTenant?.id || "",
    widgetId: values.showWidget?.id || "",
    enabled: values.enabled,
    configOverride: safeJsonParse(values.configOverride, "configOverride"),
  };
}

export function getTenantWidgetAccessCreateVariables(
  values: TenantWidgetAccessValuesType
): TenantWidgetAccessCreateMutationVariables {
  return {
    tenantId: values.showTenant?.id || "",
    widgetId: values.showWidget?.id || "",
    enabled: values.enabled,
    configOverride: safeJsonParse(values.configOverride, "configOverride"),
  };
}
